import { useState } from "react";
import axios from "axios";
import {
  Flame, Clock, Zap, ArrowUpRight, ArrowDownRight, AlertTriangle, ListChecks, GraduationCap, Radar, RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AuraLightweightChart from "@/components/AuraLightweightChart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chart decimal precision per instrument (matches the ICT engine's own tick size)
const CHART_ASSET_MAP = {
  "ES=F":     { decimals: 2 },
  "NQ=F":     { decimals: 2 },
  "EURUSD=X": { decimals: 5 },
  "GC=F":     { decimals: 2 },
  "XAUUSD=X": { decimals: 2 },
  "GBPUSD=X": { decimals: 5 },
  "JPY=X":    { decimals: 3 },
  "GBPJPY=X": { decimals: 3 },
};

// Color/emoji lookup by conviction tier
export const tierStyle = {
  EXTREME: { color: "bg-red-500/20 text-red-400 border-red-500/40", bar: "bg-red-500" },
  HIGH:    { color: "bg-orange-500/20 text-orange-400 border-orange-500/40", bar: "bg-orange-500" },
  MEDIUM:  { color: "bg-amber-500/20 text-amber-400 border-amber-500/40", bar: "bg-amber-500" },
  LOW:     { color: "bg-slate-500/20 text-slate-400 border-slate-500/40", bar: "bg-slate-500" },
  MINIMAL: { color: "bg-slate-500/10 text-slate-500 border-slate-500/20", bar: "bg-slate-600" },
};

export const statusBadge = {
  ENTRY_ARMED: { label: "ENTRY ARMED", className: "bg-emerald-500/30 text-emerald-200 border-emerald-400/60 animate-pulse" },
  IN_ZONE:     { label: "IN ZONE",     className: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  APPROACHING: { label: "APPROACHING", className: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  WAITING:     { label: "WAITING",     className: "bg-slate-500/20 text-slate-400 border-slate-500/40" },
};

export const biasStyle = {
  BULLISH: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  BEARISH: "text-red-400 border-red-500/40 bg-red-500/10",
  NEUTRAL: "text-slate-400 border-slate-500/40 bg-slate-500/10",
};

export const factorLabels = {
  fib_236: "Fib 23.6%", fib_382: "Fib 38.2%", fib_500: "Fib 50%",
  fib_618: "Fib 61.8% (Golden)", fib_786: "Fib 78.6%",
  vp_poc: "Volume POC", vp_val: "Value Area Low", vp_vah: "Value Area High",
  order_block: "Order Block",
  order_block_bull: "Bullish Order Block",
  order_block_bear: "Bearish Order Block",
  elliott_wave: "Elliott Wave Target",
  vwap: "VWAP", vwap_1sd: "VWAP ±1σ", vwap_2sd: "VWAP ±2σ",
  asia_high: "Asia High", asia_low: "Asia Low",
  prev_day_high: "Prior Week High", prev_day_low: "Prior Week Low",
  round_number: "Round Number",
  fvg_confirm: "FVG Displacement",
  unhit_london_high: "Unhit London High", unhit_london_low: "Unhit London Low",
  unhit_ny_high: "Unhit NY High", unhit_ny_low: "Unhit NY Low",
};

// Tick-size formatter (from backend INSTRUMENTS config)
const TICK_SIZE = {
  "ES=F": 0.25, "NQ=F": 0.25, "EURUSD=X": 0.00001,
  "GC=F": 0.10, "XAUUSD=X": 0.01,
  "GBPUSD=X": 0.00001, "JPY=X": 0.001, "GBPJPY=X": 0.001,
};

export const fmt = (val, symbol) => {
  if (val == null || !Number.isFinite(Number(val))) return "—";
  const tick = TICK_SIZE[symbol] || 0.01;
  const decimals = tick < 0.001 ? 5 : tick < 0.01 ? 4 : tick < 1 ? 2 : 2;
  return Number(val).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

// Weekly Forecast cascade: Weekly zone -> Daily confirm -> 4H confirm -> 1H entry
export const CASCADE_STEPS = ["Weekly Zone", "Daily Confirm", "4H Confirm", "1H Entry"];
export const CASCADE_PROGRESS = {
  no_data: 0, no_weekly_zone: 0, ranging_no_trend: 0,
  daily_not_confirmed: 1,
  "4h_not_confirmed": 2,
  awaiting_price: 3,
  in_zone_awaiting_1h_trigger: 3,
  entry_armed: 4,
};
export const CASCADE_STAGE_LABEL = {
  no_data: "No weekly data yet",
  no_weekly_zone: "No weekly confluence zone found",
  ranging_no_trend: "Ranging - no clear trend, skipped (quality over quantity)",
  daily_not_confirmed: "Weekly zone found — awaiting Daily structure/FVG confirmation",
  "4h_not_confirmed": "Daily confirmed — awaiting 4H FVG displacement",
  awaiting_price: "4H confirmed — waiting for price to reach the weekly zone",
  in_zone_awaiting_1h_trigger: "Price in the zone — waiting for a 1H entry trigger",
  entry_armed: "Entry armed on a 1H trigger",
};

export function Metric({ label, value, sub, color, testid }) {
  return (
    <div className="text-right" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

// A single Buy Zone or Sell Zone with the confluence reasoning behind it -
// answers "why is this valid" directly instead of just showing a number.
export function ZoneReasonCard({ zone, symbol, kind }) {
  const isBuy = kind === "buy";
  if (!zone) {
    return (
      <div className="text-[11px] text-muted-foreground italic py-1" data-testid={`no-${kind}-zone-${symbol}`}>
        No clear {isBuy ? "buy" : "sell"} zone identified right now.
      </div>
    );
  }
  const tier = tierStyle[zone.conviction?.tier] || tierStyle.LOW;
  const reasons = (zone.factors || []).map((f) => factorLabels[f.name] || f.name);
  return (
    <div className="space-y-1.5" data-testid={`${kind}-zone-${symbol}`}>
      <div className="flex items-center justify-between text-xs">
        <span className={`uppercase tracking-wider font-semibold ${isBuy ? "text-emerald-400" : "text-red-400"}`}>
          {isBuy ? "Buy Zone" : "Sell Zone"} · {zone.poi_type}
        </span>
        <span className={tier.color + " px-2 py-0.5 rounded border text-[10px] font-medium"}>
          {zone.conviction?.emoji} {zone.conviction?.score_pct}%
        </span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-lg font-medium">{fmt(zone.center, symbol)}</span>
        <span className="text-xs text-muted-foreground">{zone.distance_pct != null ? `${zone.distance_pct}% away` : ""}</span>
      </div>
      <div className="text-[11px] text-muted-foreground">
        Entry area: <span className="text-foreground">{fmt(zone.band_low, symbol)} → {fmt(zone.band_high, symbol)}</span>
      </div>
      {zone.fvg_confirmation && (
        <div className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/30 inline-block">
          Confirmed by {zone.fvg_confirmation.type} FVG on {zone.fvg_confirmation.timeframe?.toUpperCase()}
        </div>
      )}
      {reasons.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {reasons.slice(0, 5).map((r, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800/70 text-slate-300 border border-slate-700">{r}</span>
          ))}
        </div>
      )}
      {reasons.length > 0 && (
        <p className="text-[10px] text-muted-foreground/80 italic">
          Valid because: {reasons.join(" + ")} ({zone.factor_count} confluence{zone.factor_count > 1 ? "s" : ""}).
        </p>
      )}
    </div>
  );
}

export function PreviousTradeCard({ previous, label, testid }) {
  const trade = previous.trade;
  const outcome = previous.outcome || {};
  const outcomeStyle = {
    hit_target: { text: "TARGET HIT", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
    hit_stop:   { text: "STOPPED OUT", cls: "bg-red-500/20 text-red-300 border-red-500/40" },
    open:       { text: "STILL OPEN",  cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
    no_entry:   { text: "NO FILL",     cls: "bg-slate-500/20 text-slate-300 border-slate-500/40" },
    no_data:    { text: "NO DATA",     cls: "bg-slate-500/20 text-slate-400 border-slate-500/40" },
    unknown:    { text: "UNKNOWN",     cls: "bg-slate-500/20 text-slate-400 border-slate-500/40" },
    error:      { text: "ERROR",       cls: "bg-slate-500/20 text-slate-400 border-slate-500/40" },
  }[outcome.outcome] || { text: "PENDING REVIEW", cls: "bg-slate-500/20 text-slate-400 border-slate-500/40" };
  const sym = trade.symbol;
  return (
    <Card className="border-cyan-500/20" data-testid={testid}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">
              {label} · Prediction Review
            </div>
            <CardTitle className="text-base mt-1">
              {trade.instrument} · {trade.direction}
            </CardTitle>
            <div className="text-xs text-muted-foreground mt-1">
              {previous.analysis_time_label} → {previous.valid_until_label}
            </div>
          </div>
          <div className="text-right space-y-1">
            <Badge className={outcomeStyle.cls}>{outcomeStyle.text}</Badge>
            {typeof outcome.pnl_r === "number" && (
              <div className={`text-sm font-semibold ${outcome.pnl_r > 0 ? "text-emerald-400" : outcome.pnl_r < 0 ? "text-red-400" : "text-slate-300"}`}>
                {outcome.pnl_r > 0 ? "+" : ""}{outcome.pnl_r}R
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="text-xs grid grid-cols-3 gap-3">
        <div className="bg-slate-800/50 rounded p-2">
          <div className="text-[10px] uppercase text-muted-foreground">Entry</div>
          <div className="font-medium">{fmt(trade.entry, sym)}</div>
        </div>
        <div className="bg-slate-800/50 rounded p-2">
          <div className="text-[10px] uppercase text-muted-foreground">Stop</div>
          <div className="font-medium text-red-400">{fmt(trade.stop, sym)}</div>
        </div>
        <div className="bg-slate-800/50 rounded p-2">
          <div className="text-[10px] uppercase text-muted-foreground">Target</div>
          <div className="font-medium text-emerald-400">{fmt(trade.target, sym)}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export function TradeCard({ trade, label, testid, dateLabel, validUntilLabel }) {
  const tier = tierStyle[trade.conviction?.tier] || tierStyle.MEDIUM;
  const isLong = trade.direction === "LONG";
  const sym = trade.symbol;
  const isWeek = /week/i.test(label);
  return (
    <Card
      className="border-2 relative overflow-hidden"
      style={{
        borderColor: isLong ? "rgba(52, 211, 153, 0.4)" : "rgba(248, 113, 113, 0.4)",
        background: "linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(30,41,59,0.7) 100%)",
      }}
      data-testid={testid}
    >
      <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 blur-3xl"
        style={{ background: isLong ? "rgb(52, 211, 153)" : "rgb(248, 113, 113)" }} />
      <CardHeader className="pb-3 relative">
        <div className="flex items-start justify-between">
          <div>
            <div className={`text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5 ${isWeek ? "text-amber-400" : "text-cyan-400"}`}>
              <Flame className="w-3.5 h-3.5" />
              {label}
            </div>
            <CardTitle className="text-2xl mt-1" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              {trade.instrument}
            </CardTitle>
            {dateLabel && (
              <div className="text-[11px] text-amber-300/90 mt-1 font-medium" data-testid={`${testid}-date`}>
                {dateLabel}{validUntilLabel ? ` · valid until ${validUntilLabel}` : ""}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              POI: <span className="text-foreground font-medium">{trade.poi_type}</span> · Hold max {trade.hold_max_days} day{trade.hold_max_days > 1 ? "s" : ""}
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{trade.conviction?.cot_adjusted_pct ?? trade.conviction?.score_pct}%</div>
            <div className="text-xs text-muted-foreground">{trade.conviction?.emoji} {trade.conviction?.tier}</div>
            {trade.conviction?.cot_adjusted_pct != null && trade.conviction.cot_adjusted_pct !== trade.conviction.score_pct && (
              <div className="text-[10px] text-cyan-400 mt-0.5">
                Base {trade.conviction.score_pct}% + COT
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 relative">
        <div className="flex items-center gap-2">
          <Badge
            className={`text-sm px-3 py-1 ${isLong ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-red-500/20 text-red-300 border-red-500/40"}`}
          >
            {isLong ? <ArrowUpRight className="w-3.5 h-3.5 mr-1" /> : <ArrowDownRight className="w-3.5 h-3.5 mr-1" />}
            {trade.direction}
          </Badge>
          {trade.cot_alignment === "confirmed" && (
            <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/40">COT confirmed</Badge>
          )}
          {trade.cot_alignment === "conflict" && (
            <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/40">COT conflict</Badge>
          )}
          {trade.structure_tags?.includes("bos_confirmed") && (
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">BOS confirmed</Badge>
          )}
          {trade.structure_tags?.includes("choch_warning") && (
            <Badge className="bg-red-500/20 text-red-300 border-red-500/40">⚠ CHoCH warning</Badge>
          )}
          {trade.structure_tags?.includes("counter_trend") && (
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">Counter-trend</Badge>
          )}
          {trade.entry_armed && (
            <Badge className="bg-emerald-500/30 text-emerald-200 border-emerald-400/60 animate-pulse" data-testid={`${testid}-entry-armed`}>
              <Zap className="w-3 h-3 mr-1" />ENTRY ARMED
            </Badge>
          )}
          <Badge variant="outline" className="ml-auto">Entry area: {trade.entry_area_label}</Badge>
        </div>

        {trade.entry_armed && trade.entry_trigger && (
          <div className="text-[11px] bg-emerald-500/10 border border-emerald-500/30 rounded p-2" data-testid={`${testid}-trigger-info`}>
            <span className="text-emerald-400 font-medium">15m confirmation:</span> {trade.entry_trigger.pattern?.replace("_", " ")} — confirmed close beyond {fmt(trade.entry_trigger.engulf_high, sym)} / {fmt(trade.entry_trigger.engulf_low, sym)}
          </div>
        )}

        <div className="grid grid-cols-3 gap-3 pt-2">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Entry</div>
            <div className="text-lg font-semibold">{fmt(trade.entry, sym)}</div>
            <div className="text-[10px] text-muted-foreground">
              {fmt(trade.entry_band_low, sym)} → {fmt(trade.entry_band_high, sym)}
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Stop</div>
            <div className="text-lg font-semibold text-red-400">{fmt(trade.stop, sym)}</div>
            <div className="text-[10px] text-muted-foreground">Invalidation</div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">{isWeek ? "Main Target" : "Target"}</div>
            <div className="text-lg font-semibold text-emerald-400">{fmt(trade.target, sym)}</div>
            <div className="text-[10px] text-muted-foreground">R:R {trade.rr_ratio}:1</div>
          </div>
        </div>

        {isWeek && trade.target_2 != null && (
          <div className="text-[11px] bg-slate-800/40 border border-slate-700/50 rounded p-2" data-testid={`${testid}-partial-tp`}>
            <span className="text-slate-300 font-medium">Optional partial take-profit:</span> {fmt(trade.target_2, sym)} (1:{trade.rr_ratio_2}) — the main thesis is the 1:{trade.rr_ratio} target above, but you can bank some profit here early if you'd rather de-risk.
          </div>
        )}

        <p className="text-xs text-muted-foreground italic pt-2 leading-relaxed border-t border-slate-700 pt-3">
          {trade.rationale}
        </p>

        {trade.confluence_breakdown?.length > 0 && (
          <div className="pt-3 border-t border-slate-700 space-y-2" data-testid={`${testid}-confluence-breakdown`}>
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold flex items-center gap-1.5">
              <ListChecks className="w-3.5 h-3.5" />
              Confluence Breakdown — What I'm Seeing
            </div>
            <div className="space-y-1.5">
              {trade.confluence_breakdown.map((b, i) => (
                <div key={i} className="flex items-start gap-2 text-xs bg-slate-800/40 rounded p-2" data-testid={`${testid}-confluence-${i}`}>
                  <Badge variant="outline" className="text-[10px] shrink-0 border-cyan-500/40 text-cyan-300 mt-0.5">
                    {b.timeframe}
                  </Badge>
                  <div>
                    <span className="font-medium text-foreground">{b.term}</span>
                    {b.level != null && <span className="text-muted-foreground"> @ {fmt(b.level, sym)}</span>}
                    {b.definition && <p className="text-muted-foreground text-[11px] mt-0.5 leading-snug">{b.definition}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {trade.senior_take && (
          <div className="pt-1" data-testid={`${testid}-senior-take`}>
            <div className="text-[11px] uppercase tracking-widest text-amber-400 font-semibold flex items-center gap-1.5 mb-1.5">
              <GraduationCap className="w-3.5 h-3.5" />
              Senior Trader's Take
              {trade.senior_take_provisional && (
                <span
                  className="ml-1 text-[9px] uppercase tracking-wide text-amber-300/70 font-normal border border-amber-500/30 rounded px-1.5 py-0.5"
                  data-testid={`${testid}-senior-take-provisional-badge`}
                >
                  Locks in 08:00 UK
                </span>
              )}
            </div>
            <p className="text-xs text-slate-200 leading-relaxed italic bg-amber-500/5 border border-amber-500/20 rounded p-3">
              "{trade.senior_take}"
            </p>
          </div>
        )}

        {CHART_ASSET_MAP[sym] && (
          <div className="pt-3 border-t border-slate-700" data-testid={`${testid}-chart`}>
            <AuraLightweightChart
              symbol={sym}
              candleSource="ict"
              showZoneToggles={false}
              decimals={CHART_ASSET_MAP[sym].decimals}
              label={trade.instrument}
              defaultTimeframe={isWeek ? "1w" : "1d"}
              overrideTradePlan={{
                bias: trade.direction,
                entry: trade.entry,
                stop: trade.stop,
                tp1: trade.target,
                tp2: trade.target_2 ?? null,
                entryZone: (trade.entry_band_low != null && trade.entry_band_high != null)
                  ? { low: trade.entry_band_low, high: trade.entry_band_high } : null,
                confluenceLevels: (trade.confluence_breakdown || []).filter((b) => b.level != null),
              }}
            />
          </div>
        )}

        {!isWeek && (
          <RescanEntryPanel symbol={sym} instrument={trade.instrument} testid={testid} />
        )}
      </CardContent>
    </Card>
  );
}

function RescanEntryPanel({ symbol, instrument, testid }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleScan = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/aura/intraday/rescan-entry/${encodeURIComponent(symbol)}`);
      setResult(res.data);
    } catch (err) {
      toast.error("Rescan failed - please try again in a moment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-3 border-t border-slate-700 space-y-3" data-testid={`${testid}-rescan-panel`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold flex items-center gap-1.5">
          <Radar className="w-3.5 h-3.5" />
          Already closed? Look again
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleScan}
          disabled={loading}
          data-testid={`${testid}-rescan-button`}
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Scanning 5m / 1m..." : "Scan For New Entry"}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        If this trade already hit target or stop, request a fresh look right now - drills into
        5-minute/1-minute price action for a continuation or pullback entry with the trend, and
        factors in the latest macro/news backdrop.
      </p>

      {result && !result.found_entry && (
        <div className="text-xs bg-slate-800/40 border border-slate-700/50 rounded p-3 text-muted-foreground" data-testid={`${testid}-rescan-no-entry`}>
          {result.message || result.error || "No fresh entry found right now."}
        </div>
      )}

      {result?.found_entry && (
        <div className="space-y-3 bg-slate-800/30 border border-cyan-500/20 rounded-lg p-3" data-testid={`${testid}-rescan-result`}>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={result.direction === "LONG" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-red-500/20 text-red-300 border-red-500/40"}>
              {result.direction === "LONG" ? <ArrowUpRight className="w-3.5 h-3.5 mr-1" /> : <ArrowDownRight className="w-3.5 h-3.5 mr-1" />}
              {result.direction}
            </Badge>
            <Badge variant="outline" className="border-cyan-500/40 text-cyan-300 capitalize">{result.setup_type}</Badge>
            <Badge variant="outline" className="text-muted-foreground">{result.timeframe_used?.toUpperCase()} trigger · Trend: {result.htf_trend}</Badge>
            <Badge
              variant="outline"
              className={result.confluence_quality === "multi_factor"
                ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}
              data-testid={`${testid}-rescan-quality-badge`}
            >
              {result.confluence_quality === "multi_factor"
                ? `Multi-Factor Zone (${(result.confluence_breakdown || []).length} confluences)`
                : "Single-Factor Fallback"}
            </Badge>
          </div>

          {result.refinement_note && (
            <p className="text-[11px] text-cyan-300/80" data-testid={`${testid}-rescan-refinement`}>
              {result.refinement_note}
            </p>
          )}

          <div className="grid grid-cols-3 gap-2">
            <div className="bg-slate-800/50 rounded p-2">
              <div className="text-[10px] uppercase text-muted-foreground">Entry</div>
              <div className="text-sm font-semibold">{fmt(result.entry, symbol)}</div>
            </div>
            <div className="bg-slate-800/50 rounded p-2">
              <div className="text-[10px] uppercase text-muted-foreground">Stop</div>
              <div className="text-sm font-semibold text-red-400">{fmt(result.stop, symbol)}</div>
            </div>
            <div className="bg-slate-800/50 rounded p-2">
              <div className="text-[10px] uppercase text-muted-foreground">Target</div>
              <div className="text-sm font-semibold text-emerald-400">
                {fmt(result.target, symbol)} <span className="text-[10px] text-muted-foreground">R:R {result.rr_ratio}:1</span>
              </div>
            </div>
          </div>

          {result.confluence_breakdown?.length > 0 && (
            <div className="space-y-1.5" data-testid={`${testid}-rescan-confluence-breakdown`}>
              {result.confluence_breakdown.map((b, i) => (
                <div key={i} className="flex items-start gap-2 text-xs bg-slate-800/40 rounded p-2" data-testid={`${testid}-rescan-confluence-${i}`}>
                  <Badge variant="outline" className="text-[10px] shrink-0 border-cyan-500/40 text-cyan-300 mt-0.5">
                    {b.timeframe}
                  </Badge>
                  <div>
                    <span className="font-medium text-slate-200">{b.term}</span>
                    {b.level != null && <span className="text-muted-foreground"> @ {fmt(b.level, symbol)}</span>}
                    <p className="text-muted-foreground">{b.definition}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {result.senior_take && (
            <p className="text-xs text-slate-200 leading-relaxed italic bg-amber-500/5 border border-amber-500/20 rounded p-3" data-testid={`${testid}-rescan-senior-take`}>
              "{result.senior_take}"
            </p>
          )}

          {CHART_ASSET_MAP[symbol] && (
            <AuraLightweightChart
              symbol={symbol}
              candleSource="ict"
              showZoneToggles={false}
              decimals={CHART_ASSET_MAP[symbol].decimals}
              label={`${instrument} · Fresh Entry`}
              defaultTimeframe={result.timeframe_used}
              overrideTradePlan={{
                bias: result.direction,
                entry: result.entry,
                stop: result.stop,
                tp1: result.target,
                entryZone: (result.entry_band_low != null && result.entry_band_high != null)
                  ? { low: result.entry_band_low, high: result.entry_band_high } : null,
                confluenceLevels: (result.confluence_breakdown || []).filter((b) => b.level != null),
                markerLabel: `New Entry ${fmt(result.entry, symbol)}`,
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function TradeSection({ meta, label, horizon, testidBase, subtitle }) {
  const pending = meta?.pending;
  const trade = meta?.trade;
  const reviewTrade = meta?.review_trade;
  const isReviewable = meta?.is_reviewable;
  const prev = meta?.previous;

  const timingLine = horizon === "week"
    ? <>Produced <span className="text-foreground font-medium">{meta.generated_at_label || meta.analysis_time_label}</span> (Friday close) · live from <span className="text-foreground font-medium">{meta.valid_from_label}</span> · valid until <span className="text-foreground font-medium">{meta.valid_until_label}</span></>
    : <>Analysis after Asian close · <span className="text-foreground font-medium">{meta.analysis_time_label}</span> · valid until <span className="text-foreground font-medium">{meta.valid_until_label}</span></>;

  return (
    <div className="space-y-3" data-testid={`${testidBase}-section`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Clock className="w-3.5 h-3.5" />
        <span>{timingLine}</span>
      </div>
      {subtitle && (
        <p className="text-xs text-amber-300/80" data-testid={`${testidBase}-subtitle`}>{subtitle}</p>
      )}

      {!pending && trade && (
        <TradeCard
          trade={trade}
          label={label}
          testid={`${testidBase}-card`}
          dateLabel={horizon === "week"
            ? `Week of ${meta.trade_date_label}`
            : meta.trade_date_label}
          validUntilLabel={meta.valid_until_label}
        />
      )}

      {!pending && !trade && (
        <Card className="border-slate-700/50 bg-slate-800/20" data-testid={`${testidBase}-no-trade`}>
          <CardContent className="py-4 flex items-start gap-3 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="text-xs text-muted-foreground">
              {horizon === "week"
                ? "No instrument has completed the full Weekly → Daily → 4H → 1H cascade this week yet - check each instrument card below for its current stage."
                : "No high-conviction setup right now - check individual instrument cards below."}
            </div>
          </CardContent>
        </Card>
      )}

      {pending && isReviewable && reviewTrade && (
        <div className="space-y-2" data-testid={`${testidBase}-review`}>
          <div className="text-[11px] uppercase tracking-widest text-purple-400 font-semibold flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            Ready for Review — goes live {meta.valid_from_label}
          </div>
          <TradeCard
            trade={reviewTrade}
            label={`${label} · Review`}
            testid={`${testidBase}-review-card`}
            dateLabel={`Week of ${meta.trade_date_label}`}
            validUntilLabel={meta.valid_until_label}
          />
        </div>
      )}

      {pending && !isReviewable && (
        <Card className="border-amber-500/30 bg-amber-500/5" data-testid={`${testidBase}-pending`}>
          <CardContent className="py-4 flex items-start gap-3 text-sm">
            <Clock className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium text-amber-300">{label} · awaiting analysis</div>
              <div className="text-xs text-muted-foreground mt-1">
                {horizon === "week"
                  ? "No instrument has completed the full Weekly → Daily → 4H → 1H cascade yet this week - check each instrument card below for its current stage."
                  : <>Next analysis at <span className="text-foreground font-medium">{meta.next_analysis_label || meta.analysis_time_label}</span>.</>}
                {prev ? " Showing last completed trade with outcome review below." : ""}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {prev && prev.trade && (
        <PreviousTradeCard
          previous={prev}
          label={horizon === "week" ? "Previous Week" : "Previous Day"}
          testid={`${testidBase}-previous-card`}
        />
      )}
    </div>
  );
}

export function TrackRecordWidget({ data }) {
  const stats = data?.stats || {};
  const history = data?.history || [];
  const n = stats.sample_size;
  const winRate = stats.win_rate_pct;
  const cumR = stats.cumulative_r;
  const avgR = stats.avg_r;
  const rColor = cumR > 0 ? "text-emerald-400" : cumR < 0 ? "text-red-400" : "text-slate-300";
  const rateColor = winRate == null ? "text-slate-400"
    : winRate >= 55 ? "text-emerald-400"
    : winRate >= 40 ? "text-amber-400" : "text-red-400";

  return (
    <Card
      className="border-cyan-500/20 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(23,37,84,0.4) 100%)" }}
      data-testid="track-record-widget"
    >
      <CardContent className="py-4 px-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
              <Zap className="w-4 h-4 text-cyan-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">
                Weekly Track Record
              </div>
              <div className="text-xs text-muted-foreground">
                Last {n} completed weekly prediction{n === 1 ? "" : "s"}
                {stats.streak_label && <span className="ml-2 text-amber-400">· {stats.streak_label}</span>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-5 flex-wrap">
            <Metric label="Win Rate" value={winRate != null ? `${winRate}%` : "—"} sub={`${stats.win_count}W / ${stats.loss_count}L`} color={rateColor} testid="stat-win-rate" />
            <Metric label="Cumulative" value={`${cumR > 0 ? "+" : ""}${cumR}R`} sub={`Best ${stats.best_r ?? "—"}R · Worst ${stats.worst_r ?? "—"}R`} color={rColor} testid="stat-cum-r" />
            <Metric label="Avg R" value={avgR != null ? `${avgR > 0 ? "+" : ""}${avgR}R` : "—"} sub={`Expectancy ${stats.expectancy_r ?? "—"}R`} color={avgR > 0 ? "text-emerald-400" : avgR < 0 ? "text-red-400" : "text-slate-300"} testid="stat-avg-r" />
            {stats.no_entry_count > 0 && (
              <Metric label="No Fill" value={stats.no_entry_count} sub={`of ${n}`} color="text-slate-400" testid="stat-no-fill" />
            )}
          </div>
        </div>

        {/* Mini sparkline of results */}
        {history.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-700/50">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Sequence (oldest → newest)</div>
            <div className="flex items-end gap-1" data-testid="track-record-sequence">
              {history.map((h, i) => {
                const win = h.outcome === "hit_target";
                const loss = h.outcome === "hit_stop";
                const nofill = h.outcome === "no_entry";
                const dot = win ? "bg-emerald-500" : loss ? "bg-red-500" : nofill ? "bg-slate-500" : "bg-amber-500";
                const height = win ? "h-8" : loss ? "h-5" : "h-3";
                return (
                  <div
                    key={i}
                    className="group relative flex flex-col items-center gap-1"
                    title={`${h.period_label} · ${h.instrument} ${h.direction} · ${h.outcome} · ${h.pnl_r > 0 ? "+" : ""}${h.pnl_r}R`}
                  >
                    <div className={`w-6 ${height} ${dot} rounded-sm opacity-90`} />
                    <span className="text-[9px] text-muted-foreground">{h.pnl_r > 0 ? "+" : ""}{h.pnl_r}</span>
                    <div className="absolute -top-14 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[10px] whitespace-nowrap z-10 pointer-events-none">
                      {h.period_label}<br/>{h.instrument} {h.direction}<br/>{h.outcome}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ErrorCard({ label, symbol }) {
  return (
    <Card data-testid={`instrument-card-${symbol}`}>
      <CardHeader>
        <CardTitle className="text-lg">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <AlertTriangle className="w-4 h-4" />
          Data temporarily unavailable
        </div>
      </CardContent>
    </Card>
  );
}
